import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';
import Icon, { IconType } from '../Icon/Icon';
import { IInvoice, IInvoiceItem, IInvoiceProduct } from './OrganizationTypes';
import { TabList, TabPanel, Tabs, Tab } from 'react-tabs';
import Button from '../_Core/Button';

interface IInvoiceEditorProps {
  onUpdated: (invoice: IInvoice) => void;
  invoice: IInvoice;
  isOwner: boolean;
  products: IInvoiceProduct[];
}

const InvoiceEditor = (props: IInvoiceEditorProps) => {
  const [form, setForm] = useState<IInvoice>(props.invoice);

  useEffect(() => {
    setForm({ ...props.invoice, CreatedAtUtc: FormatDate(props.invoice.CreatedAtUtc, 'input', true) });
  }, [props.invoice]);

  const subscriptionDateValue = (value?: string) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.substring(0, 10);
    return FormatDate(value, 'input', true) ?? '';
  };

  const save = () => {
    let url = `../OrganizationData/UpdateInvoice`;
    const payload = { ...form, SubscriptionValidThruUtc: form.SubscriptionValidThruUtc || null };
    store.server.postApi<ServerResponse<IInvoice>>(url, payload).then((x) => {
      if (x.Success) {
        props.onUpdated(x.Value);
        setForm(x.Value);
        toast.success(`Invoice updated!`);
      } else {
        toast.error(`${x.Message}`, { autoClose: false });
      }
    });
  };

  const unblock = () => {
    if(!window.confirm(`Are you sure you want to unblock this site for 2 weeks?`)) {
        return;
    }
    let url = `../OrganizationData/UnblockSite`;
    store.server.postApi<ServerResponse<IInvoice>>(url, form).then((x) => {
      if (x.Success) {
        props.onUpdated(x.Value);
        setForm(x.Value);
        toast.success(`Site unblocked for 2 weeks!`);
      } else {
        toast.error(`${x.Message}`, { autoClose: false });
      }
    });
  };

  const removeFee = () => {
    if(!window.confirm(`Are you sure you want to remove the card processing fee for this invoice?`)) {
        return;
    }
    let url = `../OrganizationData/RemoveInvoiceFee`;
    store.server.postApi<ServerResponse<IInvoice>>(url, form).then((x) => {
      if (x.Success) {
        props.onUpdated(x.Value);
        setForm(x.Value);
        toast.success(`Card processing fee removed!`);
      } else {
        toast.error(`${x.Message}`, { autoClose: false });
      }
    });
  };

  const remove = () => {
    if (!window.confirm(`Are you sure you want to remove this invoice?`)) {
      return;
    }
    let url = `../OrganizationData/UpdateInvoice`;
    let rqst = { ...form };
    rqst.Remove = true;
    store.server.postApi<ServerResponse<IInvoice>>(url, rqst).then((x) => {
      if (x.Success) {
        props.onUpdated(x.Value);
        setForm(x.Value);
        toast.dark(`Invoice removed!`);
      } else {
        toast.error(`${x.Message}`, { autoClose: false });
      }
    });
  };

  const itemUpdated = (item: IInvoiceItem) => {
    let invoice = { ...form };
    let found = false;

    if (!invoice.Items) invoice.Items = [];

    console.log(item);

    for (let i = 0; i < (invoice.Items?.length ?? 0); i++) {
      let x = invoice.Items[i];
      if (x.PublicId === item.PublicId) {
        invoice.Items[i] = item;

        console.log(invoice.Items);
        found = true;
        break;
      }
    }

    if (!found) {
      invoice.Items.push(item);
    }

    setForm(invoice);
  };

  return (
    <>
      <Tabs>
        <TabList>
          <Tab>Invoice</Tab>
          <Tab>Misc</Tab>
        </TabList>
        <TabPanel>
          <div className="form-horizontal">
            <h3>Order </h3>
            <hr />
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="eifg-Type">
                Type
              </label>
              <div className="col-sm-6">
                <input
                  id={'eifg-Type'}
                  type={'text'}
                  maxLength={250}
                  value={form.Type}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, Type: e.target.value });
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="eifg-Method">
                Method
              </label>
              <div className="col-sm-6">
                <input
                  id={'eifg-Method'}
                  type={'text'}
                  maxLength={250}
                  value={form.Method ?? undefined}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, Method: e.target.value });
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="eifg-PurchaseOrderName">
                Purchase Order Name
              </label>
              <div className="col-sm-6">
                <input
                  id={'eifg-PurchaseOrderName'}
                  type={'text'}
                  maxLength={250}
                  value={form.PurchaseOrderName ?? undefined}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, PurchaseOrderName: e.target.value });
                  }}
                />
              </div>
            </div>
            {props.isOwner && (
              <div className="form-group">
                <label className="control-label col-sm-4" htmlFor="eifg-SubscriptionValidThru">
                  Subscription expires once paid
                </label>
                <div className="col-sm-6">
                  <input
                    id="eifg-SubscriptionValidThru"
                    type="date"
                    value={subscriptionDateValue(form.SubscriptionValidThruUtc)}
                    className="form-control"
                    onChange={(e) => {
                      setForm({ ...form, SubscriptionValidThruUtc: e.target.value || undefined });
                    }}
                  />
                  <div className="help-block">
                    Leave blank to extend the license one year when this invoice is paid. A date replaces that extension and is the subscription end. On an invoice that is already paid, saving applies the date now.
                  </div>
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="eifg-Amount">
                Amount
              </label>
              <div className="col-sm-6">
                <input
                  id={'eifg-Amount'}
                  type={'number'}
                  min={0}
                  max={100000}
                  step={1}
                  value={form.Amount}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, Amount: +e.target.value });
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="44eifg-CreatedAtUtc">
                Invoice Date
              </label>
              <div className="col-sm-6">
                <input
                  id={'44eifg-CreatedAtUtc'}
                  type={'date'}
                  defaultValue={FormatDate(form.CreatedAtUtc, 'input') ?? undefined}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, CreatedAtUtc: e.target.value });
                    console.log(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="eifg-DueAtUtc">
                Due
              </label>
              <div className="col-sm-6">
                <input
                  id={'eifg-DueAtUtc'}
                  type={'date'}
                  defaultValue={FormatDate(form.DueAtUtc, 'input', true) ?? undefined}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, DueAtUtc: e.target.value });
                    console.log(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="eifg-Notes">
                Notes
              </label>
              <div className="col-sm-6">
                <textarea
                  id={'eifg-Notes'}
                  rows={10}
                  maxLength={10000}
                  value={form.Notes}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, Notes: e.target.value });
                  }}
                />
              </div>
            </div>

            {form.Items &&
              form.Items.length > 0 &&
              form.Items.map((x, i) => {
                return <InvoiceItemEditor invoice={x} isOwner={props.isOwner} onUpdated={itemUpdated} key={`iie-k-${i}`} id={x.PublicId} />;
              })}
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="eifg-NewItem">
                New Item
              </label>
              <div className="col-sm-6">
                <select
                  id={'eifg-NewItem'}
                  value={form.NewItem ?? 'none'}
                  className={`form-control`}
                  onChange={(e) => {
                    setForm({ ...form, NewItem: e.target.value });
                  }}>
                  <option value={'none'}>select</option>
                  {props.products.map((x, i) => {
                    return (
                      <option key={`iie-anp-${i}`} value={x.PublicId}>
                        {x.Name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    save();
                  }}>
                  <Icon type={IconType.save} /> Save
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    remove();
                  }}>
                  <Icon type={IconType.trashO} /> Remove
                </button>
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel>
            {props.isOwner && <div className='alert alert-default soft-border'>
                <h4>Unblock Site For 2 Weeks</h4>
                <p>This will unblock the site for 2 weeks so they can access it while they sort out payment.</p>
                <Button type='check' text='Unblock' onClick={unblock} />
            </div>}
            {props.isOwner && <div className='alert alert-default soft-border'>
                <h4>Remove Card Process Fee</h4>
                {props.invoice.WaveCardProcessingFee ? <p><strong>Note:</strong> The card processing fee has already been removed for this invoice.</p> : null}
                <p>This will remove the card processing fee for this invoice. So they can pay without being charged the fee.</p>
                <Button type='check' text='Remove' onClick={removeFee} />
            </div>}
        </TabPanel>
      </Tabs>
    </>
  );
};

interface IInvoiceItemEditorProps {
  onUpdated: (item: IInvoiceItem) => void;
  invoice: IInvoiceItem;
  isOwner: boolean;
  id: string;
}

const InvoiceItemEditor = (props: IInvoiceItemEditorProps) => {
  const [form, setForm] = useState<IInvoiceItem>(props.invoice);

  useEffect(() => {
    setForm(props.invoice);
  }, [props.invoice]);

  const save = (item: IInvoiceItem) => {
    props.onUpdated(item);
  };

  return (
    <>
      <div className="form-horizontal invoice-item">
        <h3>Item {form.ItemName}</h3>
        <hr />
        <div className="form-group">
          <label className="control-label col-sm-4" htmlFor="eifg-Name">
            Name
          </label>
          <div className="col-sm-6">
            <input
              id={props.id + 'eifg-Name'}
              type={'text'}
              maxLength={500}
              value={form.Name}
              className={`form-control`}
              onChange={(e) => {
                save({ ...form, Name: e.target.value });
              }}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="control-label col-sm-4" htmlFor="eifg-Cost">
            Cost
          </label>
          <div className="col-sm-6">
            <input
              id={props.id + 'eifg-Cost'}
              type={'number'}
              maxLength={250}
              min={0}
              max={10000}
              step={1}
              value={form.Cost}
              className={`form-control`}
              onChange={(e) => {
                save({ ...form, Cost: +e.target.value });
              }}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="control-label col-sm-4" htmlFor="eifg-Quantity">
            Quantity
          </label>
          <div className="col-sm-6">
            <input
              id={props.id + 'eifg-Quantity'}
              type={'number'}
              maxLength={250}
              min={0}
              max={10000}
              step={1}
              value={form.Quantity}
              className={`form-control`}
              onChange={(e) => {
                save({ ...form, Quantity: +e.target.value });
              }}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="control-label col-sm-4" htmlFor="eifg-Notes">
            Notes
          </label>
          <div className="col-sm-6">
            <textarea
              id={'eifg-Notes'}
              rows={3}
              maxLength={10000}
              value={form.Notes}
              className={`form-control`}
              onChange={(e) => {
                save({ ...form, Notes: e.target.value });
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceEditor;
